import { COLORS, FONT_FAMILY } from './theme'
import type { BacktestMetrics, MonteCarloStats } from './types'

interface MetricsComparisonProps {
  backtest: BacktestMetrics
  monteCarlo: MonteCarloStats
}

interface MetricRow {
  key: keyof BacktestMetrics
  label: string
  format: 'pct' | 'num'
  mcKey: keyof MonteCarloStats
}

const METRICS: MetricRow[] = [
  { key: 'netProfitPct', label: 'Net Profit %', format: 'pct', mcKey: 'netProfit' },
  { key: 'winRate', label: 'Win Rate', format: 'pct', mcKey: 'winRate' },
  { key: 'profitFactor', label: 'Profit Factor', format: 'num', mcKey: 'profitFactor' },
  { key: 'maxDrawdownPct', label: 'Max DD %', format: 'pct', mcKey: 'maxDrawdown' },
  { key: 'sharpe', label: 'Sharpe', format: 'num', mcKey: 'sharpe' },
  { key: 'riskOfRuin', label: 'Risk of Ruin', format: 'pct', mcKey: 'riskOfRuin' as keyof MonteCarloStats },
]

function fmt(value: number, format: 'pct' | 'num'): string {
  if (!Number.isFinite(value)) return '—'
  if (format === 'pct') return `${(value * 100).toFixed(2)}%`
  return value.toFixed(3)
}

function p5Color(value: number, label: string): string {
  if (label === 'Max DD %' || label === 'Risk of Ruin') {
    return value > 0.05 ? COLORS.secondary : value > 0.02 ? COLORS.warning : COLORS.success
  }
  if (label === 'Profit Factor') {
    return value < 1 ? COLORS.secondary : value < 1.5 ? COLORS.warning : COLORS.success
  }
  return value < 0 ? COLORS.secondary : COLORS.success
}

export function MetricsComparison({ backtest, monteCarlo }: MetricsComparisonProps) {
  return (
    <div className="w-full h-full flex flex-col">
      <h3 className="font-bold text-[14px] mb-3" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
        Comparativa Backtest vs Monte Carlo
      </h3>
      <div className="flex-1 overflow-auto">
        {METRICS.map((metric) => {
          const backtestValue = backtest[metric.key] as number
          let mcMedian: number
          let mcP5: number

          if (metric.mcKey === 'riskOfRuin') {
            mcMedian = monteCarlo.riskOfRuin
            mcP5 = monteCarlo.riskOfRuin
          } else {
            const dist = monteCarlo[metric.mcKey] as { median: number; p5: number; p95: number }
            mcMedian = dist.median
            mcP5 = dist.p5
          }

          return (
            <div key={metric.key} className="mb-3">
              <div className="grid grid-cols-[1fr_80px_80px_80px] items-center gap-2 text-[11px]">
                <span className="font-bold" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
                  {metric.label}
                </span>
                <span className="text-right" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
                  {fmt(backtestValue, metric.format)}
                </span>
                <span className="text-center" style={{ color: COLORS.border, fontFamily: FONT_FAMILY }}>
                  →
                </span>
                <div className="text-right">
                  <span className="font-bold block" style={{ color: COLORS.primary, fontFamily: FONT_FAMILY }}>
                    {fmt(mcMedian, metric.format)}
                  </span>
                  <span className="text-[9px] block" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
                    MC Median
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-[1fr_80px_80px_80px] items-center gap-2 mt-1">
                <div />
                <div />
                <div />
                <div
                  className="text-right text-[11px] font-bold"
                  style={{ color: p5Color(mcP5, metric.label), fontFamily: FONT_FAMILY }}
                >
                  5th: {fmt(mcP5, metric.format)}
                </div>
              </div>
              <div className="mt-1 h-[2px] w-full" style={{ backgroundColor: COLORS.grid }}>
                <div
                  className="h-full"
                  style={{
                    width: `${Math.min(100, Math.max(0, mcMedian * 100))}%`,
                    backgroundColor: COLORS.primary,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
